import { EventType, UserType, SearchEventType, PushLogType } from "globals/types/models.ts";
import TYPES from "config/store/types.ts";
import { Dispatch } from "redux";
import { tryAsyncFunc } from "globals/functions/async.ts";
import ajax from "config/ajax.ts";

import { addOrReplaceArr, deleteArr } from "utils/transformer.ts";
import { EnumUserTypeType } from "globals/types/globals.ts";

// A push log enriched with the associated user (as returned by the admin endpoint)
export interface AdminPushLogType extends PushLogType {
  user?: Pick<UserType, "id" | "name" | "email"> | null;
}

// An active push token (i.e. a reachable device). It may be linked to a user, or be anonymous
// (no user yet, identified only by its sessionId).
export interface PushRecipientType {
  id: number;
  userId: number | null;
  name: string | null;
  email: string | null;
  sessionId: string | null;
  type: string;
  deviceName: string | null;
}

export interface PushStatsType {
  totalUsers: number;
  usersWithPushToken: number;
  activeTokenCount: number;
  downloadRate: number;
}

// Define pagination type
interface PaginationType {
  page: number;
  limit: number;
  totalCount: number;
  totalPages: number;
}

// Define sorting type
interface SortingType {
  field: string;
  direction: "ASC" | "DESC";
}

// Define a type for the slice state
interface State {
  events: SearchEventType[];
  eventsPagination: PaginationType;
  eventsSorting: SortingType;
  users: UserType[];
  pushLogs: AdminPushLogType[];
  pushRecipients: PushRecipientType[];
  pushStats: PushStatsType;
}

// Define the initial state using that type
const initialState: State = {
  events: [],
  eventsPagination: {
    page: 1,
    limit: 10,
    totalCount: 0,
    totalPages: 0
  },
  eventsSorting: {
    field: "id",
    direction: "DESC"
  },
  users: [],
  pushLogs: [],
  pushRecipients: [],
  pushStats: {
    totalUsers: 0,
    usersWithPushToken: 0,
    activeTokenCount: 0,
    downloadRate: 0
  }
};

export const adminReducer = (state = initialState, action: { payload: any, type: keyof typeof TYPES }): State => {
  switch (action.type) {
    case TYPES.ADMIN_GET_USERS:
      return {
        ...state,
        users: action.payload
      };
    case TYPES.ADMIN_GET_USER_BY_ID:
      return {
        ...state,
        users: addOrReplaceArr([...state.users], action.payload)
      };
    case TYPES.ADMIN_CREATED_USER:
      return {
        ...state,
        users: addOrReplaceArr([...state.users], action.payload)
      };
    case TYPES.ADMIN_UPDATED_USER:
      return {
        ...state,
        users: addOrReplaceArr([...state.users], action.payload)
      };
    case TYPES.ADMIN_DELETED_USER:
      return {
        ...state,
        users: deleteArr([...state.users], { id: action.payload })
      };

    case TYPES.ADMIN_GET_PUSH_LOGS:
      return {
        ...state,
        pushLogs: action.payload.pushLogs
      };

    case TYPES.ADMIN_GET_PUSH_RECIPIENTS:
      return {
        ...state,
        pushRecipients: action.payload.recipients,
        pushStats: action.payload.stats
      };

    case TYPES.ADMIN_GET_EVENTS:
      return {
        ...state,
        events: action.payload.events,
        eventsPagination: action.payload.pagination,
        eventsSorting: action.payload.sorting || state.eventsSorting
      };
    case TYPES.ADMIN_GET_EVENT_BY_UUID:
      return {
        ...state,
        events: addOrReplaceArr([...state.events], action.payload)
      };
    case TYPES.ADMIN_CREATED_EVENT:
      return {
        ...state,
        events: addOrReplaceArr([...state.events], action.payload)
      };
    case TYPES.ADMIN_UPDATED_EVENT:
      return {
        ...state,
        events: addOrReplaceArr([...state.events], action.payload)
      };
    case TYPES.ADMIN_DELETED_EVENT:
      return {
        ...state,
        events: deleteArr([...state.events], { id: action.payload })
      };
    default:
      return state;
  }
};


export const adminActions = {
  getEvents: (search?: string, page: number = 1, limit: number = 10, sortField?: string, sortDirection?: "ASC" | "DESC") => async (dispatch: Dispatch) => {
    const [res, err] = await tryAsyncFunc(ajax.get("/connected/admin/events", {
      params: {
        search: search || "",
        page,
        limit,
        sortField,
        sortDirection
      }
    }));

    if (err) {
      if (window.Sentry) {
        window.Sentry.captureException(err);
      }
      return;
    }

    dispatch({
      type: TYPES.ADMIN_GET_EVENTS,
      payload: {
        events: res.data.events,
        pagination: res.data.pagination
      }
    });
    return res.data;
  },
  getEventByUuid: (uuid: string) => async (dispatch: Dispatch) => {
    const [res, err] = await tryAsyncFunc(ajax.get(`/connected/admin/events/uuid/${uuid}`));

    if (err) {
      if (window.Sentry) {
        window.Sentry.captureException(err);
      }
      throw err;
    }


    dispatch({
      type: TYPES.ADMIN_GET_EVENT_BY_UUID,
      payload: res.data.event
    });
    return res.data.event;
  },
  createdEvent: (event: EventType) => async (dispatch: Dispatch) => {
    dispatch({
      type: TYPES.ADMIN_CREATED_EVENT,
      payload: event
    });
  },
  updatedEvent: (event: EventType) => async (dispatch: Dispatch) => {
    dispatch({
      type: TYPES.ADMIN_UPDATED_EVENT,
      payload: event
    });
  },
  deletedEvent: (id: number) => async (dispatch: Dispatch) => {
    dispatch({
      type: TYPES.ADMIN_DELETED_EVENT,
      payload: id
    });
  },

  getUsers: (search?: string, type?: EnumUserTypeType | null) => async (dispatch: Dispatch) => {
    const [res, err] = await tryAsyncFunc(ajax.get("/connected/admin/users", {
      params: {
        search: search || "",
        type: type || ""
      }
    }));

    if (err) {
      if (window.Sentry) {
        window.Sentry.captureException(err);
      }
      return;
    }

    dispatch({
      type: TYPES.ADMIN_GET_USERS,
      payload: res.data.users
    });
    return res.data.users;
  },
  getPublicUserById: (id: number | string) => async (dispatch: Dispatch) => {
    const [res, err] = await tryAsyncFunc(ajax.get(`/connected/admin/users/id/${id}`));

    if (err) {
      if (window.Sentry) {
        window.Sentry.captureException(err);
      }
      throw err;
    }


    dispatch({
      type: TYPES.ADMIN_GET_USER_BY_ID,
      payload: res.data.user
    });
    return res.data.user;
  },
  createdUser: (user: UserType) => async (dispatch: Dispatch) => {
    dispatch({
      type: TYPES.ADMIN_CREATED_USER,
      payload: user
    });
  },
  updatedUser: (user: UserType) => async (dispatch: Dispatch) => {
    dispatch({
      type: TYPES.ADMIN_UPDATED_USER,
      payload: user
    });
  },
  deletedUser: (id: number) => async (dispatch: Dispatch) => {
    dispatch({
      type: TYPES.ADMIN_DELETED_USER,
      payload: id
    });
  },

  getPushLogs: () => async (dispatch: Dispatch) => {
    const [res, err] = await tryAsyncFunc(ajax.get("/connected/admin/push-notifications"));

    if (err) {
      if (window.Sentry) {
        window.Sentry.captureException(err);
      }
      return;
    }

    dispatch({
      type: TYPES.ADMIN_GET_PUSH_LOGS,
      payload: {
        pushLogs: res.data.pushLogs
      }
    });
    return res.data;
  },
  getPushRecipients: () => async (dispatch: Dispatch) => {
    const [res, err] = await tryAsyncFunc(ajax.get("/connected/admin/push-notifications/recipients"));

    if (err) {
      if (window.Sentry) {
        window.Sentry.captureException(err);
      }
      return;
    }

    dispatch({
      type: TYPES.ADMIN_GET_PUSH_RECIPIENTS,
      payload: {
        recipients: res.data.recipients,
        stats: res.data.stats
      }
    });
    return res.data;
  },
  //auto-select reachable users whose last known location falls within the given radius (km)
  getRecipientsAround: (latitude: number, longitude: number, radiusKm: number) => async () => {
    const [res, err] = await tryAsyncFunc(ajax.post("/connected/admin/push-notifications/recipients/around", {
      latitude,
      longitude,
      radiusKm
    }));

    if (err) {
      throw err;
    }

    return res.data as { success: boolean, pushTokenIds: number[] };
  },
  sendPushNotification: (titleFr: string, titleEn: string, bodyFr: string, bodyEn: string, pushTokenIds: number[], priority: "normal" | "high" = "normal", eventId: number | null = null) => async () => {
    const [res, err] = await tryAsyncFunc(ajax.post("/connected/admin/push-notifications", {
      titleFr,
      titleEn,
      bodyFr,
      bodyEn,
      pushTokenIds,
      priority,
      ...(eventId ? { eventId } : {})
    }));

    if (err) {
      throw err;
    }

    return res.data;
  }
};
